import { noop } from "es-toolkit";
import { useState, useCallback, useEffect } from "react";

import { localStorage, useLocalStorage } from "@web-monorepo/safe-browser-storage";
import { USE_PRECANARY_STORAGE_KEY } from "@web-monorepo/fetchers";
import env from "@web-monorepo/shared/utils/env";
declare global {
  interface Window {
    Playwright?: object;
  }
}

//
// dev toolbar visibility
//
const setIsDevToolsEnabled = (value: string) => {
  localStorage.setItem("devTools.enable", value);
};

const getIsDevToolsEnabled = () => {
  return localStorage.getItem("devTools.enable") === "true" || false;
};

export const useIsDevToolsEnabled = () => {
  if (typeof window === "undefined") return { isDevToolsEnabled: false };
  // tracks if dev tools are available to user or not
  const isRunningOnE2E = !!window.Playwright;
  const hasAccessToDevTools = !isRunningOnE2E;

  let devToolsQSValue: string | null = env.isDev ? "true" : null;
  if (window.location?.href?.includes("dev-tools=true")) devToolsQSValue = "true";
  if (window.location?.href?.includes("dev-tools=false")) devToolsQSValue = "false";

  if (hasAccessToDevTools && devToolsQSValue) {
    setIsDevToolsEnabled(devToolsQSValue);
  }

  // tracks if dev tools should be displayed or not
  return { isDevToolsEnabled: hasAccessToDevTools && getIsDevToolsEnabled() };
};

//
// dev toolbar panel dock position
//
export const PANEL_DISPLAY_SIDES = {
  Left: "left",
  Right: "right",
  Bottom: "bottom",
} as const;

export type PanelDisplaySide = (typeof PANEL_DISPLAY_SIDES)[keyof typeof PANEL_DISPLAY_SIDES];

const setPanelDisplaySide = (value: PanelDisplaySide) => {
  localStorage.setItem("devTools.panelSide", value);
};

const getPanelDisplaySide = (): PanelDisplaySide => {
  const stored = localStorage.getItem("devTools.panelSide");
  if (stored === "left" || stored === "right" || stored === "bottom") return stored;
  return PANEL_DISPLAY_SIDES.Right;
};

const usePanelDisplaySide = () => {
  const [panelDisplaySide, setPanelDisplaySideState] = useState(getPanelDisplaySide());

  const _setPanelDisplaySide = useCallback((value: PanelDisplaySide) => {
    setPanelDisplaySideState(value);
    setPanelDisplaySide(value);
  }, []);

  return { panelDisplaySide, setPanelDisplaySide: _setPanelDisplaySide };
};

//
// dev toolbar feature switch overrides
//

// list of event handlers registered to get called when a new event is logged.
// we need this so the feature switches section UI updates when we change a feature switch value
let onFeatureSwitchOverridesChangeHandlers: ((fsOverrides: Record<string, unknown>) => void)[] = [];

function useOnFeatureSwitchOverridesChangeHandler(
  setFeatureSwitchOverridesState: (state: Record<string, unknown>) => void,
) {
  useEffect(() => {
    onFeatureSwitchOverridesChangeHandlers = [
      ...onFeatureSwitchOverridesChangeHandlers,
      setFeatureSwitchOverridesState,
    ];

    return () => {
      onFeatureSwitchOverridesChangeHandlers = onFeatureSwitchOverridesChangeHandlers.filter(
        (h) => h !== setFeatureSwitchOverridesState,
      );
    };
  }, [setFeatureSwitchOverridesState]);
}

function notifyOnFeatureSwitchOverridesChange(fsOverrides: Record<string, unknown>) {
  for (const handler of onFeatureSwitchOverridesChangeHandlers) {
    handler(fsOverrides);
  }
}

const setFeatureSwitchOverrides = (fsOverrides: Record<string, unknown>) => {
  localStorage.setItem("devTools.featureSwitchOverrides", JSON.stringify(fsOverrides));
  notifyOnFeatureSwitchOverridesChange(fsOverrides);
};

const getFeatureSwitchOverrides = (): Record<string, unknown> => {
  try {
    const featureSwitchOverrides: string | null = localStorage.getItem("devTools.featureSwitchOverrides");
    return featureSwitchOverrides ? (JSON.parse(featureSwitchOverrides) as Record<string, unknown>) : {};
    // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
  } catch {
    return {};
  }
};

// Loads feature switch override values from localStorage and re-runs all existing
// hook instances when new overrides are saved.
// This is needed because featureSwitches pod uses one instance of this hook
// to load the override values, and the dev toolbar feature switches section uses
// another instance to set new values for the feature switches, so we need to trigger
// re-runs of all instances so that the dev toolbar feature switches section displays
// the updated values, otherwise, the user would select a value for the feature switch and
// the dev toolbar UI would not update.
const useFeatureSwitchOverrides = () => {
  const [featureSwitchOverrides, setFeatureSwitchOverridesState] =
    useState<Record<string, unknown>>(getFeatureSwitchOverrides());

  useOnFeatureSwitchOverridesChangeHandler(setFeatureSwitchOverridesState);

  const _setFeatureSwitchOverride = useCallback(
    (switchName: string, value: string) => {
      const updatedFeatureSwitchOverrides: Record<string, unknown> = {
        ...featureSwitchOverrides,
        [switchName]: value,
      };
      setFeatureSwitchOverridesState(updatedFeatureSwitchOverrides);
      setFeatureSwitchOverrides(updatedFeatureSwitchOverrides);
    },
    [featureSwitchOverrides],
  );

  const clearFeatureSwitchOverride = useCallback(
    (switchName: string) => {
      const { [switchName]: _, ...updatedFeatureSwitchOverrides } = featureSwitchOverrides;
      setFeatureSwitchOverridesState(updatedFeatureSwitchOverrides);
      setFeatureSwitchOverrides(updatedFeatureSwitchOverrides);
    },
    [featureSwitchOverrides],
  );

  const clearAllFeatureSwitchOverrides = useCallback(() => {
    const updatedFeatureSwitchOverrides = {};
    setFeatureSwitchOverridesState(updatedFeatureSwitchOverrides);
    setFeatureSwitchOverrides(updatedFeatureSwitchOverrides);
  }, []);

  const hasFeatureSwitchOverrides = Object.keys(featureSwitchOverrides).length > 0;

  // we don't want to have feature switch overrides if user has disabled the dev tools
  // or doesn't have access to them, no matter what is stored in localStorage
  const { isDevToolsEnabled } = useIsDevToolsEnabled();
  if (!isDevToolsEnabled) {
    return {
      featureSwitchOverrides: {},
      hasFeatureSwitchOverrides: false,
      setFeatureSwitchOverride: noop,
      clearFeatureSwitchOverride: noop,
      clearAllFeatureSwitchOverrides: noop,
    };
  }

  return {
    featureSwitchOverrides,
    hasFeatureSwitchOverrides,
    setFeatureSwitchOverride: _setFeatureSwitchOverride,
    clearFeatureSwitchOverride,
    clearAllFeatureSwitchOverrides,
  };
};

//
// dev toolbar selected section
//
const setSelectedSection = (value: string) => {
  localStorage.setItem("devTools.selectedSection", value);
};

const getSelectedSection = (fallback: string): string => {
  return localStorage.getItem("devTools.selectedSection") ?? fallback;
};

const useSelectedSection = (fallback: string) => {
  const [selectedSection, setSelectedSectionState] = useState(getSelectedSection(fallback));

  const _setSelectedSection = useCallback((value: string) => {
    setSelectedSectionState(value);
    setSelectedSection(value);
  }, []);

  return [selectedSection, _setSelectedSection] as const;
};

//
// React Query DevTools toggle
//
let onReactQueryToggleChangeHandlers: ((enabled: boolean) => void)[] = [];

function notifyReactQueryToggleChange(enabled: boolean) {
  for (const handler of onReactQueryToggleChangeHandlers) {
    handler(enabled);
  }
}

const getReactQueryDevToolsEnabled = (): boolean => {
  return localStorage.getItem("devTools.reactQueryEnabled") === "true";
};

const setReactQueryDevToolsEnabled = (value: boolean) => {
  localStorage.setItem("devTools.reactQueryEnabled", value ? "true" : "false");
  notifyReactQueryToggleChange(value);
};

const useReactQueryDevToolsEnabled = () => {
  const [enabled, setEnabledState] = useState(getReactQueryDevToolsEnabled());

  useEffect(() => {
    onReactQueryToggleChangeHandlers = [...onReactQueryToggleChangeHandlers, setEnabledState];
    return () => {
      onReactQueryToggleChangeHandlers = onReactQueryToggleChangeHandlers.filter((h) => h !== setEnabledState);
    };
  }, []);

  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value);
    setReactQueryDevToolsEnabled(value);
  }, []);

  return { reactQueryDevToolsEnabled: enabled, setReactQueryDevToolsEnabled: setEnabled };
};

//
// usePrecanary environment toggle
//
const useUsePrecanaryEnabled = () => {
  const [enabled, setEnabled] = useLocalStorage<boolean>(USE_PRECANARY_STORAGE_KEY, false);
  return { usePrecanaryEnabled: enabled, setUsePrecanaryEnabled: setEnabled };
};

export const DevToolsSettings = {
  useIsDevToolsEnabled,
  usePanelDisplaySide,
  useSelectedSection,
  useFeatureSwitchOverrides,
  useReactQueryDevToolsEnabled,
  useUsePrecanaryEnabled,
};
